import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Button from '@atlaskit/button'
import Toggle from '@atlaskit/toggle'
import Breadcrumbs, { BreadcrumbsItem } from '@atlaskit/breadcrumbs'
import AddIcon from '@atlaskit/icon/glyph/add'
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel'
import GraphLineIcon from '@atlaskit/icon/glyph/graph-line'
import {
  fetchReleasesAsync,
  fetchActiveReleasesAsync,
  fetchArchivedReleasesAsync,
  archiveReleaseAsync,
  unarchiveReleaseAsync,
  deleteReleaseAsync,
  updateReleaseAsync,
  cloneReleaseAsync,
  pollCloneStatusAsync,
  selectReleases,
  selectReleasesLoading,
  selectReleasesError,
  selectCloningReleases
} from '../../store/slices/releaseSlice'
import { ReleaseCard } from './ReleaseCard'
import { CreateReleaseModal } from './CreateReleaseModal'
import { EditReleaseModal } from './EditReleaseModal'
import { ConfirmDeleteModal } from '../FolderBrowser/ConfirmDeleteModal'
import ReportModal from '../reports/ReportModal'
import { useNotification } from '../../contexts/NotificationContext'
import { ReleaseListSkeleton } from '../common/ReleaseListSkeleton'

/**
 * RELEASE LIST PAGE
 *
 * Displays all releases for the selected project.
 * Features:
 * - Toggle to show/hide archived releases
 * - Create new release (empty or clone from existing)
 * - Archive/unarchive releases
 * - Delete releases
 * - Click to open release in Folder Browser
 */
function ReleaseList() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { selectedProject, isLoadingProjects } = useOutletContext()
  const { showError, showSuccess, showInfo } = useNotification()

  // Redux state - ensure releases is always an array
  const releasesFromStore = useSelector(selectReleases)
  const releases = Array.isArray(releasesFromStore) ? releasesFromStore : []
  const loading = useSelector(selectReleasesLoading)
  const error = useSelector(selectReleasesError)
  const cloningReleases = useSelector(selectCloningReleases)

  // Polling interval refs for clone jobs
  const pollingIntervalsRef = useRef({})

  // Local state
  const [showArchived, setShowArchived] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingRelease, setEditingRelease] = useState(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [deletingRelease, setDeletingRelease] = useState(null)

  // Report modal state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [reportingRelease, setReportingRelease] = useState(null)

  // Fetch releases when project changes or toggle changes
  useEffect(() => {
    if (selectedProject?.value) {
      if (showArchived) {
        // Show only archived releases
        dispatch(fetchArchivedReleasesAsync(selectedProject.value))
      } else {
        // Show only active releases
        dispatch(fetchActiveReleasesAsync(selectedProject.value))
      }
    }
  }, [dispatch, selectedProject, showArchived])

  // Handle toggle change
  const handleToggleArchived = () => {
    setShowArchived(!showArchived)
  }

  // Handle create release
  const handleCreateRelease = () => {
    setIsCreateModalOpen(true)
  }

  // Handle open release
  const handleOpenRelease = (releaseId) => {
    navigate(`/releases/${releaseId}`)
  }

  // Handle archive release
  const handleArchiveRelease = (releaseId) => {
    dispatch(archiveReleaseAsync(releaseId))
  }

  // Handle unarchive release
  const handleUnarchiveRelease = (releaseId) => {
    dispatch(unarchiveReleaseAsync(releaseId))
  }

  // Handle delete release - opens confirmation modal
  const handleDeleteRelease = (release) => {
    setDeletingRelease(release)
    setIsDeleteModalOpen(true)
  }

  // Confirm delete release
  const handleConfirmDeleteRelease = async () => {
    if (deletingRelease?.id) {
      await dispatch(deleteReleaseAsync(deletingRelease.id)).unwrap()
    }
  }

  // Close delete modal
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false)
    setDeletingRelease(null)
  }

  // Start polling for a clone job status
  const startPolling = useCallback((jobId, releaseId) => {
    // Safety: don't start duplicate polls
    if (pollingIntervalsRef.current[jobId]) return

    let pollCount = 0
    const maxPolls = 40 // 40 * 3s = 2 minutes max

    const intervalId = setInterval(async () => {
      pollCount++

      try {
        const status = await dispatch(pollCloneStatusAsync(jobId)).unwrap()

        if (status.status === 'COMPLETED') {
          clearInterval(intervalId)
          delete pollingIntervalsRef.current[jobId]

          // Rename "Clone" to "Copy" in the cloned release name
          if (status.clonedRelease?.name) {
            const updatedName = status.clonedRelease.name.replace(/\(Clone\)$/, '(Copy)')
            if (updatedName !== status.clonedRelease.name) {
              dispatch(updateReleaseAsync({
                releaseId: status.clonedRelease.id,
                updates: { name: updatedName }
              }))
            }
          }

          showSuccess('Clone Complete', 'Release has been cloned successfully.')
        } else if (status.status === 'FAILED') {
          clearInterval(intervalId)
          delete pollingIntervalsRef.current[jobId]
          showError('Clone Failed', status.errorMessage || 'Failed to clone release.')
        }
      } catch (err) {
        console.error('Error polling clone status:', err)
      }

      // Safety timeout: stop polling after 2 minutes
      if (pollCount >= maxPolls) {
        clearInterval(intervalId)
        delete pollingIntervalsRef.current[jobId]
        showError('Clone Timeout', 'Clone is taking longer than expected. Please refresh the page to check.')
      }
    }, 3000)

    pollingIntervalsRef.current[jobId] = intervalId
  }, [dispatch, showSuccess, showError])

  // Cleanup polling intervals on unmount
  useEffect(() => {
    return () => {
      Object.values(pollingIntervalsRef.current).forEach(clearInterval)
    }
  }, [])

  // Handle clone release - starts async clone job and polls for completion
  // Called from both the context menu (releaseId only) and CreateReleaseModal (with options)
  const handleCloneRelease = async (releaseId, options = null) => {
    try {
      const payload = options ? { releaseId, options } : releaseId
      const jobResponse = await dispatch(cloneReleaseAsync(payload)).unwrap()
      showInfo('Cloning Release', 'Clone in progress. This may take a moment for large releases.')
      startPolling(jobResponse.jobId, releaseId)
    } catch (error) {
      console.error('Failed to start clone:', error)
      const errorMessage = error?.message || 'Failed to start clone. Please try again.'
      showError('Clone Release Failed', errorMessage)
    }
  }

  // Handle edit release - opens edit modal
  const handleEditRelease = (release) => {
    setEditingRelease(release)
    setIsEditModalOpen(true)
  }

  // Handle close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false)
    setEditingRelease(null)
  }

  // Handle mark as released - updates release status to 'Released'
  const handleMarkAsReleased = (releaseId) => {
    dispatch(updateReleaseAsync({
      releaseId,
      updates: { status: 'Released' }
    }))
  }

  // Handle mark as active - updates release status to 'Active'
  const handleMarkAsActive = (releaseId) => {
    dispatch(updateReleaseAsync({
      releaseId,
      updates: { status: 'Active' }
    }))
  }

  // Handle view report - opens report modal for the release
  const handleViewReport = (release) => {
    setReportingRelease(release)
    setIsReportModalOpen(true)
  }

  // Handle view project report - opens report modal at project scope.
  // Leaving `reportingRelease` null is the signal that this is a project report;
  // the render site derives `initialLevel` from that.
  const handleViewProjectReport = () => {
    setReportingRelease(null)
    setIsReportModalOpen(true)
  }

  // Close report modal
  const handleCloseReportModal = () => {
    setIsReportModalOpen(false)
    setReportingRelease(null)
  }

  // Separate active and archived releases
  const activeReleases = releases.filter(r => !r.archived)
  const archivedReleases = releases.filter(r => r.archived)

  // Loading state - show shimmer skeleton matching the page layout
  if (isLoadingProjects || loading) {
    return <ReleaseListSkeleton />
  }

  // Error state
  if (error) {
    return (
      <div
        className="release-list-page"
        style={{
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <p style={{ color: 'var(--ds-text-danger, #DE350B)' }}>Error loading releases</p>
        <p style={{ color: 'var(--ds-text-subtlest, #6B778C)', fontSize: '14px' }}>{error}</p>
        <Button
          appearance="primary"
          onClick={() => dispatch(fetchReleasesAsync(selectedProject?.value))}
          style={{ marginTop: '16px' }}
        >
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="release-list-page">
      {/* Header - white toolbar above blue content area, with subtle bottom shadow */}
      <div className="release-page-header">
        {/* Breadcrumb - shows project key only, consistent with FolderBrowser.
            Current page — preventDefault stops Atlaskit's default href="#" from blanking the route. */}
        <Breadcrumbs>
          <BreadcrumbsItem
            text={selectedProject?.key || 'Project'}
            onClick={(e) => e.preventDefault()}
          />
          <BreadcrumbsItem
            text="Releases"
            onClick={(e) => e.preventDefault()}
          />
        </Breadcrumbs>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Show Archived Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '14px',
              color: 'var(--ds-text-subtle, #5E6C84)'
            }}>
              Show Archived
            </span>
            <Toggle
              isChecked={showArchived}
              onChange={handleToggleArchived}
            />
          </div>

          {/*
            Project-level report entry point. Placed next to the "Show Archived"
            toggle because this page is the natural home for project-scoped
            actions — per-release reports still live on each release card.
            Disabled until a project is selected so the modal has an id to work with.
          */}
          <Button
            appearance="subtle"
            iconBefore={<GraphLineIcon label="" size="small" />}
            onClick={handleViewProjectReport}
            isDisabled={!selectedProject?.value}
          >
            Project Report
          </Button>

          {/* New Release Button */}
          <Button
            appearance="primary"
            iconBefore={<AddIcon label="Add" size="small" />}
            onClick={handleCreateRelease}
          >
            New Release
          </Button>
        </div>
      </div>

      {/* Content - Jira blue-tinted background with scrollable release cards */}
      <div className="release-list-content">
        {/* Empty State */}
        {releases.length === 0 && (
          <div
            className="empty-state"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px',
              textAlign: 'center'
            }}
          >
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: 'var(--ds-background-neutral, #F4F5F7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <EditorPanelIcon label="Releases" size="large" primaryColor="var(--ds-text-subtlest, #6B778C)" />
            </div>

            <h2 style={{
              fontSize: '20px',
              fontWeight: 500,
              color: 'var(--ds-text, #172B4D)',
              margin: '0 0 8px 0'
            }}>
              No releases yet
            </h2>

            <p style={{
              fontSize: '14px',
              color: 'var(--ds-text-subtlest, #6B778C)',
              margin: '0 0 24px 0',
              maxWidth: '400px'
            }}>
              Create your first release to get started.
            </p>

            <Button
              appearance="primary"
              iconBefore={<AddIcon label="Add" size="small" />}
              onClick={handleCreateRelease}
            >
              Create First Release
            </Button>
          </div>
        )}

        {/* Active Releases - shown when toggle is OFF */}
        {!showArchived && activeReleases.length > 0 && (
          <div className="release-section">
            <div className="release-cards" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {activeReleases.map(release => (
                <ReleaseCard
                  key={release.id}
                  release={release}
                  onOpen={() => handleOpenRelease(release.id)}
                  onEdit={() => handleEditRelease(release)}
                  onArchive={() => handleArchiveRelease(release.id)}
                  onMarkReleased={() => handleMarkAsReleased(release.id)}
                  onMarkActive={() => handleMarkAsActive(release.id)}
                  onClone={() => handleCloneRelease(release.id)}
                  onDelete={() => handleDeleteRelease(release)}
                  onViewReport={() => handleViewReport(release)}
                  isCloning={!!cloningReleases[release.id]}
                />
              ))}
            </div>
          </div>
        )}

        {/* Archived Releases - shown when toggle is ON, visually separated with divider */}
        {showArchived && archivedReleases.length > 0 && (
          <div className="release-section release-section-archived">
            <h2>Archived Releases</h2>

            <div className="release-cards" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {archivedReleases.map(release => (
                <ReleaseCard
                  key={release.id}
                  release={release}
                  onOpen={() => handleOpenRelease(release.id)}
                  onEdit={() => handleEditRelease(release)}
                  onUnarchive={() => handleUnarchiveRelease(release.id)}
                  onClone={() => handleCloneRelease(release.id)}
                  onDelete={() => handleDeleteRelease(release)}
                  onViewReport={() => handleViewReport(release)}
                  isArchived
                  isCloning={!!cloningReleases[release.id]}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Release Modal */}
      <CreateReleaseModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        projectId={selectedProject?.value}
        existingReleases={releases}
        onCloneRelease={handleCloneRelease}
      />

      {/* Edit Release Modal */}
      <EditReleaseModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        release={editingRelease}
      />

      {/* Delete Release Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDeleteRelease}
        title="Delete Release"
        itemName={deletingRelease?.name || 'release'}
        itemType="release"
        warningMessage="This will permanently delete the release, all folders, and all scenarios. This action cannot be undone."
        requireTypeConfirmation={true}
      />

      {/*
        Report Modal — dual-purpose. If `reportingRelease` is set we open a
        release-level report; otherwise (triggered via the header button) we
        open a project-level report for the currently selected project.
      */}
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={handleCloseReportModal}
        initialLevel={reportingRelease ? 'release' : 'project'}
        projectId={selectedProject?.value}
        releaseId={reportingRelease?.id}
      />
    </div>
  )
}

export { ReleaseList }
